"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useState, useCallback } from "react";
import { Menu, XIcon, ShoppingBag, Phone, Mail, Clock } from "lucide-react";
import { SiWhatsapp, SiInstagram, SiFacebook, SiWechat, SiXiaohongshu, SiTiktok, SiPinterest } from "@icons-pack/react-simple-icons";
import { Badge } from "@/components/ui/badge";
import { useHydratedCartCount } from "@/hooks/useHydratedCartCount";
import { CartSheet } from "@/components/cart/CartSheet";
import { config } from "@/lib/config";
import { brand } from "@/lib/brand";
import { SOCIAL_COLORS } from "@/lib/social-colors";
import { SocialLink } from "@/components/SocialLink";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  AnnouncementBar,
  type AnnouncementInitial,
} from "@/components/layout/AnnouncementBar";
import { HeaderBanners } from "@/components/layout/HeaderBanners";
// Lean primary nav — the core pages only. Home is reached via the logo; the
// SEO landing pages (delivery/shipping/occasion) still live in the footer,
// which is the reliably-crawled global internal-link hub (dropdown/sheet links
// are portaled and not always in the SSR HTML). The Journal (blog) is surfaced
// here for discoverability — it carries internal link equity to the shop.
const primaryNav = [
  { href: "/products", label: "Shop" },
  { href: "/flowers-for", label: "Occasions" },
  { href: "/gallery", label: "Gallery" },
  { href: "/events", label: "Events" },
  { href: "/collabs", label: "Collabs" },
  { href: "/blog", label: "Journal" },
  { href: "/reviews", label: "Reviews" },
  { href: "/about", label: "About" },
  { href: "/faq", label: "FAQ" },
  { href: "/enquire", label: "Contact" },
];

const baseColor = "var(--header-fg)";
const socialLinks = [
  { href: config.whatsapp, icon: SiWhatsapp, label: "WhatsApp", hoverColor: SOCIAL_COLORS.whatsapp.icon },
  { href: config.instagram, icon: SiInstagram, label: "Instagram", hoverColor: baseColor },
  { href: config.facebook, icon: SiFacebook, label: "Facebook", hoverColor: SOCIAL_COLORS.facebook.icon },
  { copyText: config.wechatId, icon: SiWechat, label: "WeChat", hoverColor: SOCIAL_COLORS.wechat.icon },
  { href: config.xiaohongshu, icon: SiXiaohongshu, label: "Xiaohongshu", hoverColor: SOCIAL_COLORS.xiaohongshu.icon },
  { href: config.tiktok, icon: SiTiktok, label: "TikTok", hoverColor: baseColor },
  { href: config.pinterest, icon: SiPinterest, label: "Pinterest", hoverColor: "#E60023" },
];

/** Desktop-only top bar with contact info and social icons */
function TopBar() {
  return (
    <div className="hidden bg-topbar-bg px-6 py-1 md:px-10 lg:block lg:px-16">
      <div className="mx-auto flex max-w-7xl items-center justify-between">
        <div className="flex items-center gap-3 font-sans text-[11px] font-medium uppercase tracking-[0.12em] text-footer-muted">
          <a
            href={`tel:${config.phone}`}
            className="flex items-center gap-1.5 transition-colors hover:text-header-fg"
          >
            <Phone className="h-3 w-3" />
            {/* Display the UK national format (07731 868223), not the raw
                E.164 `config.phone` — that stays in the `tel:` href, which is
                where the +44 form belongs. The footer already did this. */}
            {config.phoneDisplay}
          </a>
          <span className="text-footer-muted/50" aria-hidden>·</span>
          <span className="flex items-center gap-1.5">
            <Clock className="h-3 w-3" />
            {brand.hours.display}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {socialLinks.map(({ href, copyText, icon: Icon, label, hoverColor }) => (
            <SocialLink
              key={label}
              href={href}
              copyText={copyText}
              className="group flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:bg-header-hover"
              style={{ ["--icon-hover" as string]: hoverColor }}
              label={label}
            >
              <Icon size={14} color={baseColor} className="shrink-0 transition-colors group-hover:text-(--icon-hover)" />
            </SocialLink>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Cart icon styled for the dark header background */
function HeaderCartIcon() {
  // Hydration-safe count (0 on first paint → no #418, real count after mount).
  const count = useHydratedCartCount();

  return (
    <CartSheet
      trigger={
        <Button
          variant="ghost"
          size="icon-lg"
          className="relative shrink-0 rounded-lg text-white shadow-none transition-colors hover:opacity-80 focus-visible:ring-header-accent lg:size-10"
          aria-label={`Cart${count > 0 ? ` (${count} items)` : ""}`}
        >
          <ShoppingBag className="size-5 lg:size-5.5" />
          {count > 0 && (
            <Badge
              variant="default"
              aria-live="polite"
              aria-atomic="true"
              className="absolute right-0.5 top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-header-bg px-1 text-[11px] font-bold leading-none"
            >
              {count > 99 ? "99+" : count}
            </Badge>
          )}
        </Button>
      }
    />
  );
}

/** Cart link for the mobile hamburger menu — styled like other nav items */
function MobileCartLink({ onNavigate }: { onNavigate: () => void }) {
  const count = useHydratedCartCount();

  return (
    <CartSheet
      trigger={
        <button
          type="button"
          onClick={onNavigate}
          className="flex w-full items-center justify-between rounded-md px-4 py-3 text-lg font-semibold text-header-muted/70 transition-colors hover:bg-header-hover hover:text-header-muted"
        >
          <span className="flex items-center gap-3">
            <ShoppingBag className="h-5 w-5" />
            Cart
          </span>
          {count > 0 && (
            <Badge
              variant="default"
              className="flex h-5 min-w-5 items-center justify-center rounded-full border-0 px-1 text-xs"
            >
              {count > 99 ? "99+" : count}
            </Badge>
          )}
        </button>
      }
    />
  );
}

export function Header({
  announcement,
  closureNotice,
}: {
  /** Server-read announcement from the (main) layout — see AnnouncementBar. */
  announcement?: AnnouncementInitial | null;
  /**
   * `<ClosureNoticeBar/>` from the (main) layout — an async server component
   * passed down as a node so it keeps its R2 read while rendering inside this
   * client component. It belongs in the header's MEASURED banner stack, not in
   * `<main>`: see the HeaderBanners doc-comment for why putting it anywhere
   * else makes `PageHero` paint over it.
   */
  closureNotice?: React.ReactNode;
}) {
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const closeMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    <header
      // Safe-area padding lives on the HEADER, not the nav, so the
      // announcement bar (first child) is pushed below the iPhone notch
      // in PWA standalone mode (`viewportFit: "cover"` from app/layout).
      // Without this, the banner sits at top: 0 of the viewport — which
      // in PWA is *behind* the notch — making it look like the banner
      // "goes under the header". The cream header bg fills the safe-area
      // zone so the system status bar reads against a consistent colour.
      className="fixed top-0 left-0 right-0 z-40 w-full overflow-x-hidden bg-header-bg border-b border-header-border shadow-[0_1px_0_0_rgba(0,0,0,0.1)] pt-[env(safe-area-inset-top)]"
    >
      {/* Banner stack — announcement strip, then any closure notice, above the
          nav. `HeaderBanners` measures the WHOLE stack into `--banner-h`, which
          is what `<main>`'s padding-top and `PageHero`'s negative margin both
          compensate for. Either bar may render null; the wrapper collapses to
          0px and the variable follows.

          The closure notice is withheld on /checkout on purpose: step 4 shows
          the same closure narrowed to the product lines actually in the cart,
          so it quotes a more accurate resume date. Showing both meant one page
          asserting two different dates. */}
      <HeaderBanners
        announcement={<AnnouncementBar initial={announcement} />}
        closureNotice={pathname.startsWith("/checkout") ? null : closureNotice}
      />

      {/* Top bar — desktop only */}
      <TopBar />

      {/* Main nav bar — `pt-1.5` only; the safe-area inset already lives on
          the header (above), so we don't double-pad it here. */}
      <div className="font-serif relative px-6 py-1.5 md:px-10 md:py-2 lg:px-16">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 md:min-h-12">
          {/* Left: brand */}
          <Link
            href={pathname.startsWith("/zh") ? "/zh" : "/"}
            className="flex shrink-0 items-center text-header-fg hover:opacity-90 min-h-11 min-w-11 justify-center"
            aria-label={pathname.startsWith("/zh") ? "The Floral Muse – 利兹花店" : "The Floral Muse – Home"}
          >
            <Image
              src="/asset/logo.webp"
              alt="The Floral Muse logo"
              width={48}
              height={48}
              // No `priority` (deliberate): the hero image on each landing
              // page is the LCP candidate; competing high-priority preloads
              // cost ~20-80ms LCP on slow networks. But without `priority`,
              // next/image defaults to loading="lazy" — wrong for an
              // always-visible header element — so opt back to eager
              // without the preload/fetchpriority competition.
              loading="eager"
              className="h-10 w-10 shrink-0 object-contain sm:h-12 sm:w-12"
            />
          </Link>

          {/* Center: nav (desktop only; tablet uses hamburger) */}
          <nav aria-label="Main navigation" className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 items-center lg:flex lg:gap-8 xl:gap-12">
            {primaryNav.map(({ href, label }) => (
              <Link
                key={href}
                href={href}
                aria-current={isActive(href) ? "page" : undefined}
                className={`relative whitespace-nowrap text-base font-medium leading-none tracking-wide transition-colors hover:text-header-fg focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-header-accent focus-visible:ring-offset-2 focus-visible:ring-offset-header-bg rounded-sm ${
                  isActive(href)
                    ? "text-header-fg after:absolute after:left-0 after:top-full after:mt-1.5 after:h-px after:w-full after:rounded-full after:bg-header-accent"
                    : "text-header-muted/80"
                }`}
              >
                {label}
              </Link>
            ))}
          </nav>

          {/* Right: cart + hamburger */}
          <div className="flex shrink-0 items-center gap-3 md:gap-4">
            {/* Mobile & tablet call button. The TopBar that carries the `tel:`
                link is `lg:block`, so below lg the header had NO phone number
                at all — on exactly the devices that can dial it. Icon-only to
                keep the bar uncluttered, with a 44×44 hit area (mobile
                touch-target rule) and the readable number in the aria-label. */}
            <a
              href={`tel:${config.phone}`}
              aria-label={`Call The Floral Muse on ${config.phoneDisplay}`}
              className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg text-header-fg transition-colors hover:bg-header-hover focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-header-accent focus-visible:ring-offset-2 focus-visible:ring-offset-header-bg lg:hidden"
            >
              <Phone className="size-5" />
            </a>

            {/* Cart icon — visible on all breakpoints */}
            <HeaderCartIcon />

            {/* Mobile & tablet: hamburger menu */}
            <div className="flex items-center gap-1 lg:hidden">
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-lg"
                    className="shrink-0 rounded-lg border-0 text-header-fg shadow-none transition-colors hover:bg-header-hover hover:text-header-fg focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-header-accent focus-visible:ring-offset-2 focus-visible:ring-offset-header-bg"
                    aria-label="Toggle menu"
                  >
                    <Menu className="size-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  showCloseButton={false}
                  className="font-serif flex h-full w-[min(320px,90vw)] flex-col overflow-x-hidden border-header-border bg-header-bg px-4 pt-4 pb-4 sm:max-w-sm lg:hidden"
                >
                  <div className="flex shrink-0 items-center justify-between">
                    <Link
                      href="/"
                      onClick={closeMenu}
                      className="flex items-center"
                      aria-label="The Floral Muse – Home"
                    >
                      <Image
                        src="/asset/logo.webp"
                        alt="The Floral Muse logo"
                        width={40}
                        height={40}
                        className="h-9 w-9 object-contain"
                      />
                    </Link>
                    <SheetClose className="flex size-11 items-center justify-center rounded-md border border-header-border/50 bg-header-bg text-header-fg transition-colors hover:bg-header-hover focus:ring-2 focus:ring-header-accent focus:ring-offset-2 focus:outline-hidden [&_svg]:size-4">
                      <XIcon />
                      <span className="sr-only">Close</span>
                    </SheetClose>
                  </div>
                  <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                  <SheetDescription className="sr-only">Site links and contact options</SheetDescription>
                  <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pt-6 [-webkit-overflow-scrolling:touch]">
                    {primaryNav.map(({ href, label }) => (
                      <Link
                        key={href}
                        href={href}
                        onClick={closeMenu}
                        aria-current={isActive(href) ? "page" : undefined}
                        className={`rounded-md px-4 py-3 text-lg font-semibold transition-colors ${
                          isActive(href)
                            ? "bg-header-hover text-header-fg border-l-2 border-header-accent"
                            : "text-header-muted/70 hover:bg-header-hover hover:text-header-muted"
                        }`}
                      >
                        {label}
                      </Link>
                    ))}
                    {/* Cart pushed to the bottom + separated by a rule so it reads
                        as a distinct action, not another nav link. Secondary pages
                        (Blog etc.) live in the always-crawled footer. */}
                    <div className="mt-auto border-t border-header-border/60 pt-2">
                      <MobileCartLink onNavigate={closeMenu} />
                    </div>
                  </nav>
                  <div className="mt-auto shrink-0 border-t border-header-border pt-4">
                    {/* Readable phone + email inside the sheet — the icon-only
                        header button can be dialled but not read/copied, and
                        reg 6 expects the email to be easily accessible. */}
                    <div className="mb-3 flex flex-col gap-1">
                      <a
                        href={`tel:${config.phone}`}
                        onClick={closeMenu}
                        className="flex min-h-11 items-center gap-3 rounded-md px-4 text-sm text-header-muted transition-colors hover:bg-header-hover hover:text-header-fg"
                      >
                        <Phone className="h-4 w-4 shrink-0" />
                        {config.phoneDisplay}
                      </a>
                      <a
                        href={`mailto:${config.contactEmail}`}
                        onClick={closeMenu}
                        className="flex min-h-11 items-center gap-3 rounded-md px-4 text-sm text-header-muted transition-colors hover:bg-header-hover hover:text-header-fg"
                      >
                        <Mail className="h-4 w-4 shrink-0" />
                        <span className="truncate">{config.contactEmail}</span>
                      </a>
                    </div>
                    <p className="mb-2 px-4 text-xs font-semibold uppercase tracking-wider text-header-accent">
                      Connect with us
                    </p>
                    <div className="flex flex-wrap gap-1 px-2">
                      {socialLinks.map(({ href, copyText, icon: Icon, label, hoverColor }) => (
                        <SocialLink
                          key={label}
                          href={href}
                          copyText={copyText}
                          onClick={closeMenu}
                          className="group flex size-11 items-center justify-center rounded-md text-header-fg transition-colors hover:bg-header-hover"
                          style={{ ["--icon-hover" as string]: hoverColor }}
                          label={label}
                        >
                          <Icon size={20} color={baseColor} className="shrink-0 transition-colors group-hover:text-(--icon-hover)" />
                        </SocialLink>
                      ))}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
